import React, { useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ChatScreen } from '../ChatScreen';
import { ChatLanguage, getChatStrings, normalizeLanguage } from '../i18n';
import { Message } from '../models';
import { UserDetailsCard } from '../Components';

/**
 * Development-only preview of the chat screen from fixtures, so every state can be seen without the API:
 *
 *   /?hotel=x&preview=empty        the greeting with example questions
 *   /?hotel=x&preview=chat         a conversation: answers with items, a long menu, an abstained answer with reception
 *   /?hotel=x&preview=states       sending, not sent, conflict, the wait line, a provider error, a reconnecting composer
 *   /?hotel=x&preview=signin       the front door with an expired-session notice
 *   /?preview=invalid              the invalid-link notice
 *
 * Add `&lang=el` for Greek and `&theme=dark` on /embed for the pinned dark theme. The fixture answers are synthetic
 * (a made-up test hotel), never client content, and this module is inert in production builds.
 */

export type PreviewScenario = 'empty' | 'chat' | 'states' | 'signin' | 'invalid';

const SCENARIOS: PreviewScenario[] = ['empty', 'chat', 'states', 'signin', 'invalid'];

export function usePreviewScenario(): PreviewScenario | undefined {
   const { search } = useLocation();
   if (process.env.NODE_ENV === 'production') return undefined;
   const value = new URLSearchParams(search).get('preview');
   return SCENARIOS.find((s) => s === value);
}

const HOTEL = 'Hotel Lycabettus';
const t = (h: number, m: number) => `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;

const noop = async () => undefined;

function fixtures(
   scenario: PreviewScenario,
   language: ChatLanguage
): {
   messages: Message[];
   isBotTyping: boolean;
   canTypeMessages: boolean;
   isConnected: boolean;
   isConnecting: boolean;
   hotelName?: string;
   suggestedQuestions?: string[];
} {
   const el = language === 'el';
   const suggestedQuestions = el
      ? ['Τι ώρα σερβίρεται το πρωινό;', 'Πόσο κοστίζει το μασάζ στο σπα;', 'Υπάρχει πάρκινγκ;']
      : ['What time is breakfast?', 'How much is a massage at the spa?', 'Is there parking?'];

   switch (scenario) {
      case 'invalid':
         return {
            messages: [{ id: 'invalid', sender: 'bot', timestamp: t(9, 12), text: getChatStrings(language).invalidLink, notice: true }],
            isBotTyping: false,
            canTypeMessages: false,
            isConnected: false,
            isConnecting: false
         };
      case 'signin':
         return {
            messages: [
               {
                  id: 'signin',
                  sender: 'userDetails-bot',
                  timestamp: t(9, 12),
                  text: React.createElement(UserDetailsCard, { onGoogleAuth: noop, noticeKey: 'sessionExpired' })
               }
            ],
            isBotTyping: false,
            canTypeMessages: false,
            isConnected: false,
            isConnecting: false
         };
      case 'empty':
         return { messages: [], isBotTyping: false, canTypeMessages: true, isConnected: true, isConnecting: false, hotelName: HOTEL, suggestedQuestions };
      case 'states':
         return {
            hotelName: HOTEL,
            suggestedQuestions,
            canTypeMessages: true,
            isConnected: false,
            isConnecting: true,
            isBotTyping: true,
            messages: [
               { id: 'u1', sender: 'user', timestamp: t(21, 4), text: 'Is the pool open at night?', clientTurnId: 'c1', deliveryStatus: 'sent' },
               { id: 'a1', sender: 'bot', timestamp: t(21, 4), text: '', clientTurnId: 'c1', outcome: 'ProviderError', isOptimistic: true },
               {
                  id: 'u2',
                  sender: 'user',
                  timestamp: t(21, 6),
                  text: 'Can I get a late checkout?',
                  clientTurnId: 'c2',
                  deliveryStatus: 'failed',
                  failureKind: 'network',
                  isOptimistic: true
               },
               { id: 'u3', sender: 'user', timestamp: t(21, 7), text: 'Do you have a gym', clientTurnId: 'c3', deliveryStatus: 'failed', failureKind: 'conflict', isOptimistic: true },
               { id: 'u4', sender: 'user', timestamp: t(21, 8), text: 'Where is the nearest metro station?', clientTurnId: 'c4', deliveryStatus: 'pending', isOptimistic: true }
            ]
         };
      case 'chat':
      default:
         return {
            hotelName: HOTEL,
            suggestedQuestions,
            canTypeMessages: true,
            isConnected: true,
            isConnecting: false,
            isBotTyping: false,
            messages: [
               { id: 'u1', sender: 'user', timestamp: t(8, 41), text: 'What time is breakfast?', clientTurnId: 'c1', deliveryStatus: 'sent' },
               {
                  id: 'a1',
                  sender: 'bot',
                  timestamp: t(8, 41),
                  text: '',
                  clientTurnId: 'c1',
                  outcome: 'Answered',
                  sources: ['faq', 'restaurant-terrace'],
                  blocks: [
                     { kind: 'text', text: 'Breakfast is served on the Terrace, on the 7th floor.' },
                     { kind: 'item', text: 'Weekdays: 07:00 to 10:30', value: '07:00 to 10:30' },
                     { kind: 'item', text: 'Weekends and holidays: 07:30 to 11:00', value: '07:30 to 11:00' },
                     { kind: 'text', text: 'Room service can bring a continental breakfast until 11:30.' }
                  ]
               },
               { id: 'u2', sender: 'user', timestamp: t(8, 43), text: 'What is on the spa menu and how much does it cost?', clientTurnId: 'c2', deliveryStatus: 'sent' },
               {
                  id: 'a2',
                  sender: 'bot',
                  timestamp: t(8, 43),
                  text: '',
                  clientTurnId: 'c2',
                  outcome: 'Answered',
                  sources: ['spa-menu', 'spa-faq', 'amenities'],
                  blocks: [
                     { kind: 'text', text: 'The spa is on the lower ground floor and is open daily from 09:00 to 21:00. Treatments are booked at reception or by dialling 4.' },
                     { kind: 'heading', text: 'Massages' },
                     { kind: 'item', text: 'Swedish massage, 50 min: €85', value: '€85' },
                     { kind: 'item', text: 'Deep tissue, 50 min: €95', value: '€95' },
                     { kind: 'item', text: 'Hot stone, 75 min: €120', value: '€120' },
                     { kind: 'item', text: 'Head, neck and shoulders, 25 min: €50', value: '€50' },
                     { kind: 'heading', text: 'Facials' },
                     { kind: 'item', text: 'Hydrating facial, 50 min: €90', value: '€90' },
                     { kind: 'item', text: 'Anti-ageing facial, 60 min: €110', value: '€110' },
                     { kind: 'heading', text: 'Facilities' },
                     { kind: 'item', text: 'Sauna and steam room: included for guests', value: 'included' },
                     { kind: 'item', text: 'Indoor pool: 07:00 to 21:00', value: '07:00 to 21:00' },
                     { kind: 'item', text: 'Gym: open 24 hours with your room key', value: '24 hours' },
                     { kind: 'text', text: 'Guests under 16 may use the pool with an adult; the sauna and treatments are for guests 16 and over.' }
                  ]
               },
               { id: 'u3', sender: 'user', timestamp: t(8, 47), text: 'Can you book me a table at the restaurant tonight at 8?', clientTurnId: 'c3', deliveryStatus: 'sent' },
               {
                  id: 'a3',
                  sender: 'bot',
                  timestamp: t(8, 47),
                  text: '',
                  clientTurnId: 'c3',
                  outcome: 'Abstained',
                  receptionContact: { phones: ['+30 210 000 0000'], emails: ['reception@example.test'] },
                  blocks: [{ kind: 'text', text: "I can't make bookings yet. Reception can reserve a table for you at the Terrace or at Kipos, the garden restaurant." }]
               }
            ]
         };
   }
}

export const PreviewChat: React.FC<{ scenario: PreviewScenario }> = ({ scenario }) => {
   const { search } = useLocation();
   const [language, setLanguage] = useState<ChatLanguage>(() => normalizeLanguage(new URLSearchParams(search).get('lang')) ?? 'en');
   const [input, setInput] = useState('');
   const strings = getChatStrings(language);
   const data = useMemo(() => fixtures(scenario, language), [scenario, language]);

   return (
      <ChatScreen
         messages={data.messages}
         isBotTyping={data.isBotTyping}
         awaitingTurns={['c4']}
         strings={strings}
         language={language}
         onLanguageChange={setLanguage}
         hotelName={data.hotelName}
         suggestedQuestions={data.suggestedQuestions}
         canTypeMessages={data.canTypeMessages}
         isConnected={data.isConnected}
         isConnecting={data.isConnecting}
         input={input}
         setInput={setInput}
         onSend={() => setInput('')}
         onRetry={() => undefined}
         onSuggestion={(q) => setInput(q)}
      />
   );
};
